import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle } from "react-bootstrap-icons";

import { removeAllSelectedFiles } from "../../../store/slices/datasets";

import { closeActiveModal } from "../../../store/slices/modals";
import { LollipopChart } from "../../Widgets/Lollipop";
import { SankeyPlot } from "../../Widgets/Sankey";
import { DatasetsItem } from "../../DatasetsItem";
import { Button } from "../../Button";

import {
  ModalContainer,
  ModalFooterWrapper,
  ModalHeading,
  ModalHeadingWrapper,
  ModalOverlay,
  ModalWrapper,
} from "../styles";
import {
  DatasetItem,
  DatasetsWrapper,
  OverviewContentExt,
  RecommendedWidgetItemWrapper,
  WidgetsCount,
  WidgetsItemsWrapper,
} from "./styles";
import {
  getCurrentPageKpi,
  getCurrentPageWidgets,
  getCurrentProjectData,
  getCurrentProjectId,
  getPageSettings,
} from "../../../store/selectors/projects";
import { AreaChart } from "../../Widgets/AreaChart";
import {
  AiKpiDto,
  AiSuggestionsDto,
  CreateAreaChartWidgetDto,
  CreateBarWidgetDto,
  CreateBubbleWidgetDto,
  CreateLineChartWidgetDto,
  CreateSparkLineChartWidgetDto,
  CreateSparkAreaChartWidgetDto,
  CreateLollipopChartWidgetDto,
  CreateMapWidgetDto,
  CreateMatrixChartWidgetDto,
  CreatePolarAreaWidgetDto,
  CreatePunchcardWidgetDto,
  CreateRadarWidgetDto,
  CreateSankeyChartWidgetDto,
  CreateScatterplotWidgetDto,
  CreateRadialBarChartWidgetDto,
  UpdateWidgetDTO,
  WidgetItem,
} from "../../../models/Widgets";
import { WidgetChartWrapper } from "../../Widgets";
import { Barchart } from "../../Widgets/Barchart";
import {
  requestCreateAndUpdateWidgets,
  requestUpdateWidgets,
  requestWidgetsSuggestions,
} from "../../../store/slices/widgets";
import { WrapperLineChart } from "../../Widgets/LineChart";
import { MatrixChart } from "../../Widgets/MatrixChart";
import { LeafletMap } from "../../Widgets/LeafletMap";
import { Loader } from "../../Loader";
import { LAYOUTS } from "../../ProjectPageLayouts/config";
import { findFirstCommonElement } from "../../../helpers/firstCommonElement";
import {
  requestCreateKpis,
  requestPageKpis,
  requestPageWidgets,
  requestUpdatePage,
  setIsLoading,
  setPageWidgets,
} from "../../../store/slices/projectPages";
import { adjustArray } from "../../../helpers/ajustArray";
import {
  getLollipopChartColors,
  getLollipopChartMarkers,
} from "../../Widgets/Lollipop/utils/getLollipopChartMarkers";
import { getBarChartFormatting } from "../../Widgets/GroupedBarChart/utils/getKeys";
import {
  getLineChartColors,
  getLineChartMarkers,
} from "../../Widgets/LineChart/utils/getLineChartMarkers";
import {
  AreaChartDefaultMarkers,
  getAreaChartDefaultColors,
} from "../../Widgets/AreaChart/utils/getGroupData";
import { CloseModal } from "../DataVizSettings/styles";
import { defaultNewWidget } from "../../../pages/ProjectPage/constants";
import { getModalFirstItem } from "../../../store/selectors/modals";
import { KPIComponentWrapper } from "../../KPIComponentWrapper";
import { PolarAreaChart } from "../../Widgets/PolarAreaChart";
import { PunchcardChart } from "../../Widgets/PunchcardChart";
import { RadarChart } from "../../Widgets/RadarChart";
import { ScatterPlot } from "../../Widgets/ScatterPlot";
import { BubbleChart } from "../../Widgets/BubbleChart";
import { extractBlockIds, LayoutI } from "../../../helpers/extractBlockIds";
import { requestComparisonWidgets } from "../../../store/slices/comparison";
import { getAiKpis } from "../../../store/selectors/widgets";
import { SparkLineChart } from "../../Widgets/SparkLineChart";
import { getSparkLineChartColors } from "../../Widgets/SparkLineChart/utils/getSparkLineChartColors";
import { SparkAreaChart } from "../../Widgets/SparkAreaChart";
import { getSparkAreaChartColors } from "../../Widgets/SparkAreaChart/utils/getSparkAreaChartColors";
import { getBubbleChartDefaultColors } from "../../Widgets/BubbleChart/utils/getGroupData";
import {
  getScatterPlotDefaultColors,
  ScatterPlotDefaultMarkers,
} from "../../Widgets/ScatterPlot/utils/getGroupData";
import { getRadarChartDefaultColors } from "../../Widgets/RadarChart/utils/getGroupData";
import { getPolarAreaChartDefaultColors } from "../../Widgets/PolarAreaChart/utils/getGroupData";
import { RadialBarChart } from "../../Widgets/RadialBarChart";
import { getRadialBarDefaultColors } from "../../Widgets/RadialBarChart/utils/getGroupData";

type ModalProps = {
  onClose: () => void;
};

export const PageRecommendedWidgets = ({ onClose }: ModalProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const dispatch = useDispatch();
  const { datasets } = useSelector(getCurrentProjectData);
  const positionItem = useSelector(getModalFirstItem);
  const id = useSelector(getCurrentProjectId);
  const currentPage = useSelector(getPageSettings);
  const widgets = useSelector(getCurrentPageWidgets);
  const [isLoading, setLocalLoading] = useState(false);
  const [kpis, setKpis] = useState<AiKpiDto[]>([]);
  const [recommendedKpis, setRecommendedKpis] = useState<AiKpiDto[]>([]);
  const [recommendedWidgets, setRecommendedWidgets] = useState<any[]>([]);
  const [selectedCharts, setSelectedCharts] = useState<string[]>([]);
  const [kpiWidgetsNr, setKpiWidgetsNr] = useState<number>(0);
  const aiKpi = useSelector(getCurrentPageKpi);
  const aiKpis = useSelector(getAiKpis);

  useEffect(() => setKpiWidgetsNr(kpis?.length === 0 && aiKpis?.length > 0 ? 1 : 0), [
    aiKpis?.length,
    kpis?.length,
  ]);

  const handleOnClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      dispatch(removeAllSelectedFiles());
      if (currentPage?.id) {
        dispatch(
          requestPageKpis({
            pageId: currentPage?.id,
            includeData: true,
          })
        );
      }
      dispatch(closeActiveModal());
    }, 400);
  };

  const getKpis = useCallback(() => {
    if (id && currentPage?.id) {
      dispatch(
        requestPageKpis({
          pageId: currentPage?.id,
          includeData: true,
          callbacks: {
            onSuccess: (data) => {
              setKpis(data?.items || []);
            },
          },
        })
      );
    }
  }, [currentPage?.id, dispatch, id]);

  useEffect(() => {
    if (id && currentPage?.id) {
      getKpis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage?.id]);

  const getSuggestions = useCallback(() => {
    if (id && currentPage?.id) {
      dispatch(
        requestWidgetsSuggestions({
          pageId: currentPage?.id,
          projectId: id,
          includeData: true,
          callbacks: {
            onSuccess: (suggestions) => {
              setRecommendedWidgets(suggestions.suggested_charts);
              setRecommendedKpis(suggestions.kpis);
              setLocalLoading(false);
              dispatch(setIsLoading(false));
            },
          },
        })
      );
    }
  }, [currentPage?.id, dispatch, id]);

  useEffect(() => {
    if (id && currentPage?.id) {
      setLocalLoading(true);
      getSuggestions();
    }
  }, [id, currentPage?.id, getSuggestions]);

  const onSelectChart = (value?: string) => {
    const exist = selectedCharts?.some((r) => r === value);
    let charts = selectedCharts;
    if (exist) {
      charts = charts?.filter((r) => r !== value);
    } else {
      charts = value ? [value, ...charts] : charts;
    }
    setSelectedCharts(charts);
  };

  const renderRecommendedKpis = () => {
    if (kpis?.length) {
      return;
    }
    const firstKpi = recommendedKpis?.at(0);
    if (!firstKpi) {
      return;
    }

    const query = `${firstKpi.name}_${firstKpi.type}_${firstKpi.query}`;
    return (
      <KPIComponentWrapper
        kpi={firstKpi}
        recommended={true}
        selected={selectedCharts?.includes(query)}
        onSelect={() => onSelectChart(query)}
      />
    );
  };

  const renderRecommendedWidgets = () => {
    return recommendedWidgets?.map(
      (widget: AiSuggestionsDto, index: number) => {
        const query =
          widget.chartType === "mapChart"
            ? widget?.layers?.at(0)?.query
            : widget.query;
        const queryFirstPart = `${widget.name}_${widget.chartType}_${query}`;
        switch (widget.chartType) {
          case "mapChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  map={true}
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <LeafletMap
                    showLegend={false}
                    recommended={true}
                    selected={selectedCharts?.includes(queryFirstPart!)}
                    currentWidget={
                      {
                        ...widget,
                        id: widget.pageId,
                        display: [],
                        arrangeBy: [],
                        palette: {},
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "matrixChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <MatrixChart
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    showLegend={true}
                    currentWidget={
                      {
                        ...widget,
                        dataFormat: {},
                        palette: {},
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "lollipopChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <LollipopChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        markers: getLollipopChartMarkers(widget),
                        formatting: getLollipopChartColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "barChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <Barchart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getBarChartFormatting(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "sankeyPlot":
            const arrangeBy = [
              ...(widget?.groupBy || []),
              ...(widget?.xAxe || []),
            ];
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <SankeyPlot
                    showLegend={true}
                    recommended={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    currentWidget={
                      {
                        ...widget,
                        display: widget.display || widget.yAxe,
                        arrangeBy: arrangeBy?.filter((r) => !!r),
                        palette: {},
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "sankeyChart":
            const arrangeByChart = [
              ...(widget?.groupBy || []),
              ...(widget?.xAxe || []),
            ];
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <SankeyPlot
                    showLegend={true}
                    recommended={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    currentWidget={
                      {
                        ...widget,
                        display: widget.display || widget.yAxe,
                        arrangeBy: arrangeByChart?.filter((r) => !!r),
                        palette: {},
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "lineChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <WrapperLineChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        markers: getLineChartMarkers(widget),
                        formatting: getLineChartColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "sparkLineChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <SparkLineChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getSparkLineChartColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "sparkAreaChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <SparkAreaChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getSparkAreaChartColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "areaChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <AreaChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        markers: AreaChartDefaultMarkers(widget),
                        formatting: getAreaChartDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "bubbleChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <BubbleChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getBubbleChartDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "scatterplotChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <ScatterPlot
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        markers: ScatterPlotDefaultMarkers(widget),
                        formatting: getScatterPlotDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "radarChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <RadarChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getRadarChartDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "punchcardChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <PunchcardChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        palette: {},
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "polarAreaChart":
            return widget?.groupBy?.length ? null : (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <PolarAreaChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getPolarAreaChartDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          case "radialBarChart":
            return (
              <RecommendedWidgetItemWrapper key={index}>
                <WidgetChartWrapper
                  recommended={true}
                  selected={selectedCharts?.includes(queryFirstPart)}
                  onSelect={() => onSelectChart(queryFirstPart)}
                >
                  <RadialBarChart
                    showLegend={true}
                    selected={selectedCharts?.includes(queryFirstPart)}
                    recommended={true}
                    currentWidget={
                      {
                        ...widget,
                        formatting: getRadialBarDefaultColors(widget),
                      } as unknown as WidgetItem
                    }
                  />
                </WidgetChartWrapper>
              </RecommendedWidgetItemWrapper>
            );
          default:
            return null;
        }
      }
    );
  };

  const updateWidgetsDashboard = () => {
    let barChartRequestData: CreateBarWidgetDto[] = [];
    let lineChartRequestData: CreateLineChartWidgetDto[] = [];
    let sparkLineChartRequestData: CreateSparkLineChartWidgetDto[] = [];
    let sparkAreaChartRequestData: CreateSparkAreaChartWidgetDto[] = [];
    let lollipopChartRequestData: CreateLollipopChartWidgetDto[] = [];
    let sankeyChartRequestData: CreateSankeyChartWidgetDto[] = [];
    let areaChartRequestData: CreateAreaChartWidgetDto[] = [];
    let matrixChartRequestData: CreateMatrixChartWidgetDto[] = [];
    let mapChartRequestData: CreateMapWidgetDto[] = [];
    let scatterplotChartRequestData: CreateScatterplotWidgetDto[] = [];
    let radarChartRequestData: CreateRadarWidgetDto[] = [];
    let punchcardChartRequestData: CreatePunchcardWidgetDto[] = [];
    let polarAreaChartRequestData: CreatePolarAreaWidgetDto[] = [];
    let bubbleChartRequestData: CreateBubbleWidgetDto[] = [];
    let radialBarChartRequestData: CreateRadialBarChartWidgetDto[] = [];

    let barChartUpdateData: UpdateWidgetDTO[] = [];
    let lineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkLineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let lollipopChartUpdateData: UpdateWidgetDTO[] = [];
    let sankeyChartUpdateData: UpdateWidgetDTO[] = [];
    let mapChartUpdateData: UpdateWidgetDTO[] = [];
    let areaChartUpdateData: UpdateWidgetDTO[] = [];
    let matrixChartUpdateData: UpdateWidgetDTO[] = [];
    let scatterplotChartUpdateData: UpdateWidgetDTO[] = [];
    let radarChartUpdateData: UpdateWidgetDTO[] = [];
    let punchcardChartUpdateData: UpdateWidgetDTO[] = [];
    let polarAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let bubbleChartUpdateData: UpdateWidgetDTO[] = [];
    let radialBarChartUpdateData: UpdateWidgetDTO[] = [];

    const layout = LAYOUTS.find((l) => l.id === currentPage.templateId);
    if (layout && selectedCharts?.length) {
      let newCharts = recommendedWidgets.filter((c) => {
        const query =
          c.chartType === "mapChart" ? c?.layers?.at(0)?.query : c.query;
        const queryFirstPart = `${c.name}_${c.chartType}_${query}`;
        return selectedCharts?.includes(queryFirstPart);
      });
      const newChartsType = newCharts?.map(
        (c: { chartType: any }) => c.chartType
      );
      const currentCharts = widgets?.items?.map((w) => w.chartType);
      let allCharts = [...currentCharts, ...newChartsType];
      let complexWidgets = allCharts?.filter((c) =>
        ["mapChart", "sankey", "sankeyChart"].includes(c)
      )?.length;
      complexWidgets = complexWidgets > 2 ? 2 : complexWidgets;
      const existKpis = aiKpi?.count > 0 ? 1 : 0;
      let numWidgets = layout.numWidgets + selectedCharts?.length + existKpis;
      let newLayout = LAYOUTS.find(
        (l) =>
          l.complexity === layout.complexity &&
          l.numWidgets === numWidgets &&
          l.numComplexWidgets === complexWidgets
      );
      if (!newLayout) {
        newLayout = LAYOUTS.find(
          (l) =>
            l.variant === layout.variant &&
            l.numWidgets === numWidgets &&
            l.numComplexWidgets === complexWidgets
        );
      }
      if (!newLayout) {
        newLayout = LAYOUTS.find(
          (l) =>
            l.numWidgets === 8 &&
            l.numComplexWidgets === complexWidgets &&
            l.variant === "c"
        );
      }
      let blocks: any = extractBlockIds(newLayout?.arranging as LayoutI);
      let widgetsCopy = [...widgets?.items];

      if (
        blocks.length >
        widgetsCopy?.length + selectedCharts?.length + existKpis
      ) {
        blocks = adjustArray(
          blocks,
          widgetsCopy?.length + selectedCharts?.length + existKpis
        );
      }

      for (let i = 0; i < blocks?.length; i++) {
        const block = blocks[i];

        if (block.blockId === 1 && aiKpi.count > 0) {
          continue;
        }
        const chartType = findFirstCommonElement(allCharts, block.widgets);
        if (chartType) {
          const index = allCharts.findIndex((c: string) => c === chartType);
          if (index !== -1) {
            allCharts.splice(index, 1);
          }
        }

        const widget = widgetsCopy?.find((w: any) => w.chartType === chartType);
        if (widget) {
          const indexWidget = widgetsCopy?.findIndex(
            (widget: any) => widget.chartType === chartType
          );
          if (indexWidget !== -1) {
            widgetsCopy?.splice(indexWidget, 1);
          }
          const blockId = block?.blockId;
          switch (widget.chartType) {
            case "mapChart":
              mapChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "areaChart":
              areaChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "lineChart":
              lineChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "sparkLineChart":
              sparkLineChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: block?.blockId?.toString(),
              });
              break;
            case "sparkAreaChart":
              sparkAreaChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: block?.blockId?.toString(),
              });
              break;
            case "barChart":
              barChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "lollipopChart":
              lollipopChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "sankey":
              sankeyChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "matrix":
              matrixChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "polarAreaChart":
              polarAreaChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "punchcardChart":
              punchcardChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "radar":
              radarChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "scatterplot":
              scatterplotChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "bubbleChart":
              bubbleChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            case "radialBarChart":
              radialBarChartUpdateData.push({
                ...widget,
                id: widget.id,
                blockId: blockId?.toString(),
              });
              break;
            default:
          }
        } else {
          const selectedChart = newCharts.find(
            (c: any) => c.chartType === chartType
          );
          newCharts = newCharts.filter((c: any) => c.chartType !== chartType);
          let descriptionChartData = {
            ...defaultNewWidget,
            description:
              selectedChart?.description || defaultNewWidget.description,
            insights: selectedChart?.insights || defaultNewWidget.insights,
          };
          const blockId =
            aiKpi?.count > 0 ? block?.blockId + 1 : block?.blockId;
          switch (selectedChart?.chartType) {
            case "mapChart":
              mapChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                display: [],
                arrangeBy: [],
                palette: {},
                datasetId: selectedChart?.layers[0]?.datasetId,
                styleId: selectedChart.styleId || "default",
                dataFormat: {},
                ...descriptionChartData,
              });
              break;
            case "areaChart":
              areaChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "lineChart":
              lineChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "sparkLineChart":
              sparkLineChartRequestData.push({
                ...selectedChart,
                blockId: block?.blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "sparkAreaChart":
              sparkAreaChartRequestData.push({
                ...selectedChart,
                blockId: block?.blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "barChart":
              barChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "lollipopChart":
              lollipopChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "sankeyChart":
              const arrangeBy = [
                ...(selectedChart?.groupBy || []),
                ...(selectedChart?.xAxe || []),
              ];
              sankeyChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                display: selectedChart?.display?.length
                  ? selectedChart.display
                  : selectedChart.yAxe,
                arrangeBy: arrangeBy?.filter((r) => !!r),
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "matrixChart":
              matrixChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                dataFormat: {},
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "scatterplotChart":
              scatterplotChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                dataFormat: {},
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "radarChart":
              radarChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                dataFormat: {},
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "punchcardChart":
              punchcardChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                dataFormat: {},
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "polarAreaChart":
              polarAreaChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                ...descriptionChartData,
              });
              break;
            case "bubbleChart":
              bubbleChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                dataFormat: {},
                palette: {},
                ...descriptionChartData,
              });
              break;
            case "radialBarChart":
              radialBarChartRequestData.push({
                ...selectedChart,
                blockId: blockId?.toString(),
                orientation: "vertical",
                ...descriptionChartData,
              });
              break;
            default:
          }
        }
      }
      const existCharts =
        barChartRequestData?.length +
        lineChartRequestData?.length +
        sparkLineChartRequestData?.length +
        sparkAreaChartRequestData?.length +
        lollipopChartRequestData?.length +
        mapChartRequestData?.length +
        sankeyChartRequestData?.length +
        areaChartRequestData?.length +
        matrixChartRequestData?.length +
        barChartUpdateData?.length +
        lineChartUpdateData?.length +
        sparkLineChartUpdateData?.length +
        sparkAreaChartUpdateData?.length +
        lollipopChartUpdateData?.length +
        sankeyChartUpdateData?.length +
        areaChartUpdateData?.length +
        matrixChartUpdateData?.length +
        mapChartUpdateData?.length +
        scatterplotChartRequestData?.length +
        scatterplotChartUpdateData?.length +
        radarChartRequestData?.length +
        radarChartUpdateData?.length +
        punchcardChartRequestData?.length +
        punchcardChartUpdateData?.length +
        polarAreaChartRequestData?.length +
        polarAreaChartUpdateData?.length +
        radialBarChartRequestData?.length +
        radialBarChartUpdateData?.length +
        bubbleChartRequestData?.length +
        bubbleChartUpdateData?.length;

      if (currentPage?.id && existCharts > 0) {
        dispatch(setIsLoading(true));
        dispatch(setPageWidgets({ items: [], count: 0 }));
        if (existCharts > 0) {
          dispatch(
            requestCreateAndUpdateWidgets({
              createBarChart: barChartRequestData,
              createLineChart: lineChartRequestData,
              createSparkLineChart: sparkLineChartRequestData,
              createSparkAreaChart: sparkAreaChartRequestData,
              createLollipopChart: lollipopChartRequestData,
              createMapChart: mapChartRequestData,
              createSankeyChart: sankeyChartRequestData,
              createAreaChart: areaChartRequestData,
              createMatrixChart: matrixChartRequestData,
              barChart: barChartUpdateData,
              lineChart: lineChartUpdateData,
              sparkLineChart: sparkLineChartUpdateData,
              sparkAreaChart: sparkAreaChartUpdateData,
              lollipopChart: lollipopChartUpdateData,
              sankeyChart: sankeyChartUpdateData,
              areaChart: areaChartUpdateData,
              matrixChart: matrixChartUpdateData,
              mapChart: mapChartUpdateData,
              bubbleChart: bubbleChartUpdateData,
              scatterplotChart: scatterplotChartUpdateData,
              polarAreaChart: polarAreaChartUpdateData,
              punchcardChart: punchcardChartUpdateData,
              radarChart: radarChartUpdateData,
              createPolarAreaChart: polarAreaChartRequestData,
              createPunchcardChart: punchcardChartRequestData,
              createRadarChart: radarChartRequestData,
              createScatterplotChart: scatterplotChartRequestData,
              createBubbleChart: bubbleChartRequestData,
              createRadialBarChart: radialBarChartRequestData,
              radialBarChart: radialBarChartUpdateData,
              pageId: currentPage?.id,
            })
          );
        }
        dispatch(
          requestUpdatePage({ ...currentPage, templateId: newLayout?.id! })
        );
        if (!existCharts) {
          dispatch(
            requestPageWidgets({
              pageId: currentPage?.id,
              includeData: true,
            })
          );
        }
        getSuggestions();
        handleOnClose();
      }
    }
  };

  const maxNumber = Math.max(...(widgets?.items).map((w) => Number(w.blockId)));

  const updateWidgetsStorytelling = () => {
    let barChartRequestData: CreateBarWidgetDto[] = [];
    let lineChartRequestData: CreateLineChartWidgetDto[] = [];
    let sparkLineChartRequestData: CreateSparkLineChartWidgetDto[] = [];
    let sparkAreaChartRequestData: CreateSparkAreaChartWidgetDto[] = [];
    let lollipopChartRequestData: CreateLollipopChartWidgetDto[] = [];
    let sankeyChartRequestData: CreateSankeyChartWidgetDto[] = [];
    let areaChartRequestData: CreateAreaChartWidgetDto[] = [];
    let matrixChartRequestData: CreateMatrixChartWidgetDto[] = [];
    let mapChartRequestData: CreateMapWidgetDto[] = [];
    let scatterplotChartRequestData: CreateScatterplotWidgetDto[] = [];
    let radarChartRequestData: CreateRadarWidgetDto[] = [];
    let punchcardChartRequestData: CreatePunchcardWidgetDto[] = [];
    let polarAreaChartRequestData: CreatePolarAreaWidgetDto[] = [];
    let bubbleChartRequestData: CreatePolarAreaWidgetDto[] = [];
    let radialBarChartRequestData: CreateRadialBarChartWidgetDto[] = [];

    let barChartUpdateData: UpdateWidgetDTO[] = [];
    let lineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkLineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let lollipopChartUpdateData: UpdateWidgetDTO[] = [];
    let sankeyChartUpdateData: UpdateWidgetDTO[] = [];
    let mapChartUpdateData: UpdateWidgetDTO[] = [];
    let areaChartUpdateData: UpdateWidgetDTO[] = [];
    let matrixChartUpdateData: UpdateWidgetDTO[] = [];
    let scatterplotChartUpdateData: UpdateWidgetDTO[] = [];
    let radarChartUpdateData: UpdateWidgetDTO[] = [];
    let punchcardChartUpdateData: UpdateWidgetDTO[] = [];
    let polarAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let bubbleChartUpdateData: UpdateWidgetDTO[] = [];
    let radialBarChartUpdateData: UpdateWidgetDTO[] = [];
    const newCharts = recommendedWidgets.filter((c) => {
      const query =
        c.chartType === "mapChart" ? c?.layers?.at(0)?.query : c.query;
      const queryFirstPart = `${c.name}_${c.chartType}_${query}`;
      return selectedCharts?.includes(queryFirstPart);
    });

    const isLastItem = positionItem === widgets.count + 1;
    newCharts.forEach((item, i) => {
      const selectedChart = item;
      let descriptionChartData = {
        ...defaultNewWidget,
        description: selectedChart?.description || defaultNewWidget.description,
        insights: selectedChart?.insights || defaultNewWidget.insights,
      };

      const blockId = String(maxNumber + 1 + i);
      switch (selectedChart?.chartType) {
        case "mapChart":
          mapChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            display: [],
            arrangeBy: [],
            palette: {},
            datasetId: selectedChart?.layers[0]?.datasetId,
            styleId: selectedChart.styleId || "default",
            dataFormat: {},
            ...descriptionChartData,
          });
          break;
        case "areaChart":
          areaChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            ...descriptionChartData,
          });
          break;
        case "lineChart":
          lineChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            ...descriptionChartData,
          });
          break;
        case "sparkLineChart":
          sparkLineChartRequestData.push({
            ...selectedChart,
            blockId: blockId?.toString(),
            ...descriptionChartData,
          });
          break;
        case "sparkAreaChart":
          sparkAreaChartRequestData.push({
            ...selectedChart,
            blockId: blockId?.toString(),
            ...descriptionChartData,
          });
          break;
        case "barChart":
          barChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            ...descriptionChartData,
          });
          break;
        case "lollipopChart":
          lollipopChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            ...descriptionChartData,
          });
          break;
        case "sankeyChart":
          const arrangeBy = [
            ...(selectedChart?.groupBy || []),
            ...(selectedChart?.xAxe || []),
          ];
          sankeyChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            display: selectedChart?.yAxe || [],
            arrangeBy: arrangeBy?.filter((r) => !!r),
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "matrixChart":
          matrixChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "scatterplotChart":
          scatterplotChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "radarChart":
          radarChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "punchcardChart":
          punchcardChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "polarAreaChart":
          polarAreaChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "bubbleChart":
          bubbleChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            dataFormat: {},
            palette: {},
            ...descriptionChartData,
          });
          break;
        case "radialBarChart":
          radialBarChartRequestData.push({
            ...selectedChart,
            blockId: blockId,
            orientation: "vertical",
            ...descriptionChartData,
          });
          break;
        default:
      }
    });
    if (!isLastItem) {
      for (let widget of widgets.items) {
        const blockId =
          widget?.blockId && parseInt(widget?.blockId) >= positionItem
            ? parseInt(widget?.blockId) + newCharts?.length
            : parseInt(widget?.blockId!);
        switch (widget.chartType) {
          case "mapChart":
            mapChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "areaChart":
            areaChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "lineChart":
            lineChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkLineChart":
            sparkLineChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkAreaChart":
            sparkAreaChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "barChart":
            barChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "lollipopChart":
            lollipopChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sankey":
            sankeyChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "matrix":
            matrixChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "polarAreaChart":
            polarAreaChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "punchcardChart":
            punchcardChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "radar":
            radarChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "scatterplot":
            scatterplotChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "bubbleChart":
            bubbleChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          case "radialBarChart":
            radialBarChartUpdateData.push({
                ...widget,
                id: widget.id,
              blockId: blockId?.toString(),
            });
            break;
          default:
        }
      }
    }

    const existCharts =
      barChartRequestData?.length +
      lineChartRequestData?.length +
      sparkLineChartRequestData?.length +
      sparkAreaChartRequestData?.length +
      lollipopChartRequestData?.length +
      mapChartRequestData?.length +
      sankeyChartRequestData?.length +
      areaChartRequestData?.length +
      matrixChartRequestData?.length +
      barChartUpdateData?.length +
      lineChartUpdateData?.length +
      sparkLineChartUpdateData?.length +
      sparkAreaChartUpdateData?.length +
      lollipopChartUpdateData?.length +
      sankeyChartUpdateData?.length +
      areaChartUpdateData?.length +
      matrixChartUpdateData?.length +
      mapChartUpdateData?.length +
      scatterplotChartRequestData?.length +
      scatterplotChartUpdateData?.length +
      radarChartRequestData?.length +
      radarChartUpdateData?.length +
      punchcardChartRequestData?.length +
      punchcardChartUpdateData?.length +
      polarAreaChartRequestData?.length +
      polarAreaChartUpdateData?.length +
      radialBarChartRequestData?.length +
      radialBarChartUpdateData?.length +
      bubbleChartRequestData?.length +
      bubbleChartUpdateData?.length;

    if (currentPage?.id && existCharts > 0) {
      dispatch(setIsLoading(true));

      if (existCharts > 0) {
        dispatch(
          requestCreateAndUpdateWidgets({
            createBarChart: barChartRequestData,
            createLineChart: lineChartRequestData,
            createSparkLineChart: sparkLineChartRequestData,
            createSparkAreaChart: sparkAreaChartRequestData,
            createLollipopChart: lollipopChartRequestData,
            createMapChart: mapChartRequestData,
            createSankeyChart: sankeyChartRequestData,
            createAreaChart: areaChartRequestData,
            createMatrixChart: matrixChartRequestData,
            barChart: barChartUpdateData,
            lineChart: lineChartUpdateData,
            sparkLineChart: sparkLineChartUpdateData,
            sparkAreaChart: sparkAreaChartUpdateData,
            lollipopChart: lollipopChartUpdateData,
            sankeyChart: sankeyChartUpdateData,
            areaChart: areaChartUpdateData,
            matrixChart: matrixChartUpdateData,
            mapChart: mapChartUpdateData,
            scatterplotChart: scatterplotChartUpdateData,
            polarAreaChart: polarAreaChartUpdateData,
            punchcardChart: punchcardChartUpdateData,
            radarChart: radarChartUpdateData,
            bubbleChart: bubbleChartUpdateData,
            createPolarAreaChart: polarAreaChartRequestData,
            createPunchcardChart: punchcardChartRequestData,
            createRadarChart: radarChartRequestData,
            createScatterplotChart: scatterplotChartRequestData,
            createBubbleChart: bubbleChartRequestData,
            createRadialBarChart: radialBarChartRequestData,
            radialBarChart: radialBarChartUpdateData,
            pageId: currentPage?.id,
            callbacks: {
              onSuccess: () => {
                getSuggestions();
                if (currentPage.id && currentPage.dashType === "comparison") {
                  dispatch(
                    requestComparisonWidgets({
                      pageId: currentPage.id,
                      measure: currentPage.comparisonMeasure?.split("=")[0],
                    })
                  );
                }
              },
            },
          })
        );
      }
      handleOnClose();
    }
  };

  const addKpis = () => {
    let barChartUpdateData: UpdateWidgetDTO[] = [];
    let lineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkLineChartUpdateData: UpdateWidgetDTO[] = [];
    let sparkAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let lollipopChartUpdateData: UpdateWidgetDTO[] = [];
    let sankeyChartUpdateData: UpdateWidgetDTO[] = [];
    let mapChartUpdateData: UpdateWidgetDTO[] = [];
    let areaChartUpdateData: UpdateWidgetDTO[] = [];
    let matrixChartUpdateData: UpdateWidgetDTO[] = [];
    let radarChartUpdateData: UpdateWidgetDTO[] = [];
    let scatterplotChartUpdateData: UpdateWidgetDTO[] = [];
    let punchcardChartUpdateData: UpdateWidgetDTO[] = [];
    let polarAreaChartUpdateData: UpdateWidgetDTO[] = [];
    let bubbleChartUpdateData: UpdateWidgetDTO[] = [];
    let radialBarChartUpdateData: UpdateWidgetDTO[] = [];

    if (["storytelling", "comparison"].includes(currentPage?.dashType)) {
      for (let chart of widgets.items) {
        let blockId = chart.blockId ? parseInt(chart.blockId) : 0;
        blockId = blockId + 1;

        switch (chart?.chartType) {
          case "mapChart":
            mapChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "areaChart":
            areaChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "lineChart":
            lineChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkLineChart":
            sparkLineChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkAreaChart":
            sparkAreaChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "barChart":
            barChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "lollipopChart":
            lollipopChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "sankeyChart":
            sankeyChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "matrixChart":
            mapChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "polarAreaChart":
            polarAreaChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "punchcardChart":
            punchcardChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "radar":
            radarChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "scatterplot":
            scatterplotChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "bubbleChart":
            bubbleChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;
          case "radialBarChart":
            radialBarChartUpdateData.push({
              ...chart,
              blockId: blockId?.toString(),
            });
            break;

          default:
        }
      }
    }

    if (currentPage?.dashType === "dashboard") {
      const layout = LAYOUTS.find((l) => l.id === currentPage.templateId)!;
      const restWidgets = [...widgets?.items];
      const currentCharts = restWidgets?.map((w) => w.chartType);
      let complexWidgets = currentCharts?.filter((c) =>
        ["mapChart", "sankey", "sankeyChart"].includes(c)
      )?.length;
      complexWidgets = complexWidgets > 2 ? 2 : complexWidgets;
      let newLayout = LAYOUTS.find(
        (l) =>
          l.complexity === layout.complexity &&
          l.numWidgets === currentCharts?.length + 1 &&
          l.numComplexWidgets === complexWidgets
      );
      if (!newLayout) {
        newLayout = LAYOUTS.find(
          (l) =>
            l.numWidgets === 8 &&
            l.numComplexWidgets === complexWidgets &&
            l.variant === "c"
        );
      }
      let blocks: any = extractBlockIds(newLayout?.arranging as LayoutI);
      if (blocks.length > currentCharts?.length + 1) {
        blocks = adjustArray(blocks, currentCharts?.length + 1);
      }
      for (let i = 0; i < blocks?.length; i++) {
        const block = blocks[i];
        if (block.blockId === 1) {
          continue;
        }
        const chartType = findFirstCommonElement(currentCharts, block.widgets);
        const index = currentCharts.findIndex((c: string) => c === chartType);
        if (index !== -1) {
          currentCharts.splice(index, 1);
        }
        const blockId = block?.blockId;
        const chart = restWidgets?.find(
          (widget: any) => widget.chartType === chartType
        );
        const indexSuggestion = restWidgets?.findIndex(
          (widget: any) => widget.chartType === chartType
        );
        if (indexSuggestion !== -1) {
          restWidgets?.splice(indexSuggestion, 1);
        }
        switch (chart?.chartType) {
          case "mapChart":
            mapChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "areaChart":
            areaChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "lineChart":
            lineChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkLineChart":
            sparkLineChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sparkAreaChart":
            sparkAreaChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "barChart":
            barChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "lollipopChart":
            lollipopChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "sankey":
            sankeyChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "matrix":
            matrixChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "scatterplot":
            scatterplotChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "radar":
            radarChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "punchcardChart":
            punchcardChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "polarAreaChart":
            polarAreaChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "bubbleChart":
            bubbleChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          case "radialBarChart":
            radialBarChartUpdateData.push({
              ...chart,
              id: chart.id,
              blockId: blockId?.toString(),
            });
            break;
          default:
        }
      }
      dispatch(
        requestUpdatePage({
          ...currentPage,
          templateId: newLayout?.id!,
        })
      );
    }

    const existCharts =
      barChartUpdateData?.length +
      lineChartUpdateData?.length +
      sparkLineChartUpdateData?.length +
      sparkAreaChartUpdateData?.length +
      lollipopChartUpdateData?.length +
      sankeyChartUpdateData?.length +
      areaChartUpdateData?.length +
      matrixChartUpdateData?.length +
      mapChartUpdateData?.length +
      radarChartUpdateData?.length +
      scatterplotChartUpdateData?.length +
      punchcardChartUpdateData?.length +
      polarAreaChartUpdateData?.length +
      radialBarChartUpdateData?.length +
      bubbleChartUpdateData?.length;

    if (currentPage?.id && existCharts > 0) {
      const existKpis = recommendedKpis.filter((c) => {
        const query = `${c.name}_${c.type}_${c.query}`;
        return selectedCharts?.includes(query);
      });
      const kpi = existKpis?.at(0);

      dispatch(setIsLoading(true));
      dispatch(setPageWidgets({ items: [], count: 0 }));
      dispatch(requestCreateKpis({ ...kpi!, pageId: currentPage?.id }));

      if (existCharts > 0) {
        dispatch(
          requestUpdateWidgets({
            barChart: barChartUpdateData,
            lineChart: lineChartUpdateData,
            sparkLineChart: sparkLineChartUpdateData,
            sparkAreaChart: sparkAreaChartUpdateData,
            lollipopChart: lollipopChartUpdateData,
            sankeyChart: sankeyChartUpdateData,
            areaChart: areaChartUpdateData,
            matrixChart: matrixChartUpdateData,
            mapChart: mapChartUpdateData,
            scatterplotChart: scatterplotChartUpdateData,
            polarAreaChart: polarAreaChartUpdateData,
            punchcardChart: punchcardChartUpdateData,
            radarChart: radarChartUpdateData,
            bubbleChart: bubbleChartUpdateData,
            radialBarChart: radialBarChartUpdateData,
            pageId: currentPage?.id,
          })
        );
      }
    }
    handleOnClose();
  };

  const addNewChart = () => {
    const existKpis = recommendedKpis.filter((c) => {
      const query = `${c.name}_${c.type}_${c.query}`;
      return selectedCharts?.includes(query);
    });

    if (existKpis?.length) {
      addKpis();
    } else {
      if (["storytelling", "comparison"].includes(currentPage?.dashType)) {
        updateWidgetsStorytelling();
      } else {
        updateWidgetsDashboard();
      }
    }
  };

  return (
    <ModalOverlay
      $centred={true}
      $isClosing={isClosing}
      onClick={handleOnClose}
    >
      <ModalWrapper
        $recommendedWidgetsStyles
        $isClosing={isClosing}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeadingWrapper>
          <ModalContainer>
            <ModalHeading> Recommended Widgets</ModalHeading>
            <WidgetsCount>
              {recommendedWidgets?.length + kpiWidgetsNr}
            </WidgetsCount>
          </ModalContainer>
          <CloseModal onClick={handleOnClose} />
        </ModalHeadingWrapper>
        <OverviewContentExt>
          <DatasetsWrapper>
            <DatasetItem>Parameters</DatasetItem>
            {datasets.map((dataset) => {
              return (
                <DatasetsItem
                  disablePreview
                  dataset={dataset}
                  key={dataset.id}
                />
              );
            })}
          </DatasetsWrapper>
          <WidgetsItemsWrapper>
            {isLoading && <Loader blur={false} />}
            {renderRecommendedKpis()}
            {renderRecommendedWidgets()}
          </WidgetsItemsWrapper>
        </OverviewContentExt>

        <ModalFooterWrapper>
          <Button
            name="Close"
            onClick={handleOnClose}
            variant="neutral"
            size="medium"
          />
          <Button
            name="Submit"
            onClick={() => addNewChart()}
            variant={"secondary"}
            disabled={!(selectedCharts && selectedCharts?.length > 0)}
            size="medium"
            icon={<CheckCircle />}
          />
        </ModalFooterWrapper>
      </ModalWrapper>
    </ModalOverlay>
  );
};
